import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LayoutDashboard, Rss, PlayCircle, ListChecks, Newspaper, Landmark, Home,
  Mail, Users, BarChart3, UserCog, Settings, ScrollText, LogOut, Globe, Megaphone, Feather, Activity,
  ShoppingBag, FileText, DollarSign, Wallet, HandCoins, Share2, Vote, BrainCircuit, Compass, Lock, PenLine,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { RouteSeo } from "@/components/Seo";

const ADMIN_ROLES = ["superadmin", "admin", "editor", "analyst", "social_manager",
  "social_media_manager", "marketing", "sales", "sales_manager", "sales_associate",
  "financial", "financial_analyst", "it"];

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, section: "dashboard" },
  { to: "/admin/sources", label: "Ingestion Sources", icon: Rss, section: "ingestion" },
  { to: "/admin/runs", label: "Ingestion Runs", icon: PlayCircle, section: "ingestion" },
  { to: "/admin/review-queue", label: "Review Queue", icon: ListChecks, section: "content" },
  { to: "/admin/articles", label: "Articles", icon: Newspaper, section: "content" },
  { to: "/admin/regulations", label: "Regulations", icon: Landmark, section: "content" },
  { to: "/admin/predictions", label: "Reader Polls", icon: Vote, section: "content" },
  { to: "/admin/homepage", label: "Homepage", icon: Home, section: "content" },
  { to: "/admin/sectors", label: "Sectors", icon: Compass, section: "content" },
  { to: "/admin/newsletters", label: "Newsletters", icon: Mail, section: "marketing" },
  { to: "/admin/social", label: "Social Posts", icon: Share2, section: "social" },
  { to: "/admin/ads", label: "Ads", icon: Megaphone, section: "sales" },
  { to: "/admin/ad-orders", label: "Ad Orders", icon: ShoppingBag, section: "sales" },
  { to: "/admin/ad-pricing", label: "Ad Pricing", icon: DollarSign, section: "sales" },
  { to: "/admin/pr-submissions", label: "PR Submissions", icon: FileText, section: "marketing" },
  { to: "/admin/authors", label: "Authors", icon: Feather, section: "authors" },
  { to: "/admin/write", label: "Write as Author", icon: PenLine, section: "authors", superadminOnly: true },
  { to: "/admin/leads", label: "Leads", icon: Users, section: "sales" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, section: "analytics" },
  { to: "/admin/traffic", label: "Traffic", icon: Activity, section: "analytics" },
  { to: "/admin/intelligence", label: "Intelligence", icon: BrainCircuit, section: "intelligence" },
  { to: "/admin/seo", label: "SEO Health", icon: Globe, section: "marketing" },
  { to: "/admin/revenue", label: "Revenue", icon: Wallet, section: "finance" },
  { to: "/admin/payouts", label: "Payouts", icon: HandCoins, section: "finance" },
  { to: "/admin/users", label: "Users", icon: UserCog, section: "users" },
  { to: "/admin/settings", label: "Settings", icon: Settings, section: "settings" },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, section: "audit" },
];

const AccessDeniedPanel = ({ section, label }: { section: string; label: string }) => {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/access-requests", { section });
      toast.success(res.data.message);
      setSent(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Request failed");
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-col items-center justify-center text-center py-24 px-6" data-testid="access-denied-panel">
      <span className="h-12 w-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4">
        <Lock className="h-5 w-5 text-red-400" />
      </span>
      <h2 className="font-heading font-bold text-white text-lg">No access to {label}</h2>
      <p className="text-gray-400 text-sm mt-2 max-w-md leading-relaxed">
        Your role doesn't include the {label} area yet. Request access in one click — the Super Admin
        gets pinged by email and Telegram instantly.
      </p>
      {sent ? (
        <p className="text-[#4ADE80] text-sm mt-5 font-mono" data-testid="access-request-sent">
          Request sent — you'll see {label} appear once it's approved.
        </p>
      ) : (
        <Button onClick={request} disabled={busy} data-testid="request-access-button"
          className="mt-5 bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
          {busy ? "Sending…" : "Request access"}
        </Button>
      )}
    </div>
  );
};

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = !!user && ADMIN_ROLES.includes(user.role);
  const { data: perms } = useQuery({
    queryKey: ["my-permissions"],
    queryFn: async () => (await api.get("/admin/me/permissions")).data,
    enabled: isStaff,
    refetchInterval: 120_000,
  });
  const allowed = (section?: string) =>
    !section || !perms || perms.permissions.includes("*") || perms.permissions.includes(section);
  const nav = NAV.filter((i) => allowed(i.section) && (!(i as any).superadminOnly || user?.role === "superadmin"));
  const currentItem = NAV.slice().sort((a, b) => b.to.length - a.to.length)
    .find((n) => n.end ? location.pathname === n.to
      : location.pathname === n.to || location.pathname.startsWith(n.to + "/"));
  const blocked = !!perms && !!currentItem && !allowed(currentItem.section);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/admin/login");
      else if (!ADMIN_ROLES.includes(user.role)) navigate("/");
      else if (user.force_password_change) navigate("/change-password");
    }
  }, [loading, user, navigate]);

  if (loading || !user || !ADMIN_ROLES.includes(user.role)) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-gray-500">Verifying access...</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="admin-layout">
      <RouteSeo />
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/10 bg-[#0a0a0a] sticky top-0 h-screen overflow-y-auto">
        <div className="p-4 border-b border-white/10">
          <Logo to="/admin" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600 mt-2">Control Center</p>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end as any}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors duration-150 ${
                  isActive ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <item.icon className="h-4 w-4" /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <button onClick={() => navigate("/")} data-testid="admin-view-site"
            className="flex items-center gap-2.5 px-3 py-2 rounded text-sm text-gray-400 hover:text-white hover:bg-white/5 w-full transition-colors">
            <Globe className="h-4 w-4" /> View public site
          </button>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout"
            className="flex items-center gap-2.5 px-3 py-2 rounded text-sm text-gray-400 hover:text-red-400 hover:bg-white/5 w-full transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
          <p className="font-mono text-[10px] text-gray-600 px-3 pt-1 truncate">{user.email}</p>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="md:hidden border-b border-white/10 bg-[#0a0a0a] p-3 flex items-center justify-between">
          <Logo to="/admin" />
          <select onChange={(e) => navigate(e.target.value)} value={window.location.pathname}
            data-testid="admin-mobile-nav"
            className="bg-[#1A1A1A] border border-white/10 text-white text-sm rounded p-2">
            {nav.map((n) => <option key={n.to} value={n.to}>{n.label}</option>)}
          </select>
        </div>
        <main className="p-4 sm:p-6 max-w-[1400px]">
          {blocked && currentItem?.section
            ? <AccessDeniedPanel section={currentItem.section} label={currentItem.label} />
            : <Outlet />}
        </main>
      </div>
    </div>
  );
}
